import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useOpenOperationRow } from './OperationNav';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { ClientInfo, EvalDataset, Submission } from '../../types';
import { DataTable, Pagination, type ColumnDef, type FilterDef, type RowMenuItem } from '@shared-ui/components';
import { QuestionSearchFilter, type QuestionSearchOption } from '@shared-ui/question-picker';
import { LatexText } from '@shared-ui/latex';
import { useComponentTiming } from '@shared-ui/perf';
import { buildQuestionDisplayText, paperNumberLabel } from '@padcanvas/shared';
import { OperationsFilterBar } from './OperationsFilterBar';
import { operationsRowMenuItems, AGENTS_FILTER_DEF, DEFAULT_AGENTS, DEFAULT_DATE_RANGE, getSinceDate, syncOperationsFiltersToUrl, readOperationsParam, userSuggestionsFromRows } from './operationsFilters';

type AnalysisFilters = {
  dateRange: string;
  subject: string;
  scoreRange: string;
  platform: string;
  userFeedback: string;
  ring: string;
  agents: string;
  userId: string;
  questionId: string;
};

const SCORE_RANGE_OPTIONS = [
  { value: '0-3', label: '0–3' },
  { value: '4-7', label: '4–7' },
  { value: '8-10', label: '8–10' },
];

const PLATFORM_OPTIONS = [
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'web', label: 'Web' },
];

const USER_FEEDBACK_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'any', label: 'Any' },
];

const RING_OPTIONS = [
  { value: 'prod', label: 'Prod' },
  { value: 'beta', label: 'Beta' },
  { value: 'devtesting', label: 'Dev Testing' },
  { value: 'guest', label: 'Guest' },
];

export function AnalysisList() {
  const openRow = useOpenOperationRow();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const OP = 'submissions'; // URL param namespace so operation tabs don't collide

  const [page, setPage] = useState(Number(readOperationsParam(searchParams, 'page', OP)) || 1);

  const [appliedFilters, setAppliedFilters] = useState<AnalysisFilters>({
    subject: readOperationsParam(searchParams, 'subject', OP) || '',
    scoreRange: readOperationsParam(searchParams, 'scoreRange', OP) || '',
    platform: readOperationsParam(searchParams, 'platform', OP) || '',
    userFeedback: readOperationsParam(searchParams, 'userFeedback', OP) || '',
    userId: readOperationsParam(searchParams, 'userId', OP) || '',
    ring: readOperationsParam(searchParams, 'ring', OP) || '',
    agents: readOperationsParam(searchParams, 'agents', OP) || DEFAULT_AGENTS,
    dateRange: readOperationsParam(searchParams, 'dateRange', OP) || DEFAULT_DATE_RANGE,
    questionId: readOperationsParam(searchParams, 'questionId', OP) || '',
  });

  const pageSize = 20;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDatasetModal, setShowDatasetModal] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [importStatus, setImportStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const applyFilters = (next: AnalysisFilters) => {
    setAppliedFilters(next);
    setPage(1);
    syncOperationsFiltersToUrl(setSearchParams, next, 1, OP);
  };

  const rowMenu = (s: Submission): RowMenuItem[] => operationsRowMenuItems({
    userId: s.user?.id,
    userLabel: s.user?.email || s.user?.name,
    questionId: s.questionId,
    appliedUserId: appliedFilters.userId,
    appliedQuestionId: appliedFilters.questionId,
    apply: next => applyFilters({ ...appliedFilters, ...next }),
  });

  const extraDefs = useMemo<FilterDef[]>(() => [
    { key: 'scoreRange', kind: 'select', placeholder: 'All Scores', options: SCORE_RANGE_OPTIONS },
    { key: 'platform', kind: 'select', placeholder: 'All Platforms', options: PLATFORM_OPTIONS },
    { key: 'userFeedback', kind: 'select', placeholder: 'All Feedback', options: USER_FEEDBACK_OPTIONS },
    { key: 'ring', kind: 'select', placeholder: 'All Rings', options: RING_OPTIONS },
    AGENTS_FILTER_DEF,
    // questionId is now a QuestionSearchFilter (search by question text) rendered
    // as a child of the filter bar, not a plain text/ID input.
  ], []);

  const handleFiltersChange = (values: Record<string, string>) => {
    const next = { ...appliedFilters, ...(values as Partial<AnalysisFilters>) };
    setAppliedFilters(next);
    setPage(1);
    syncOperationsFiltersToUrl(setSearchParams, next, 1, OP);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    syncOperationsFiltersToUrl(setSearchParams, appliedFilters, newPage, OP);
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['submissions', page, appliedFilters],
    queryFn: () =>
      adminApi.getSubmissions({
        page,
        pageSize,
        subject: appliedFilters.subject || undefined,
        scoreRange: appliedFilters.scoreRange || undefined,
        platform: appliedFilters.platform || undefined,
        userFeedback: appliedFilters.userFeedback || undefined,
        userId: appliedFilters.userId || undefined,
        ring: appliedFilters.ring || undefined,
        agents: appliedFilters.agents || undefined,
        questionId: appliedFilters.questionId || undefined,
        sinceDate: getSinceDate(appliedFilters.dateRange),
      }),
  });

  // Splits the page-load number into "waiting on this list" vs the rest, so a
  // slow load is attributable rather than just a total. See Monitoring → Page Perf.
  useComponentTiming('solution-analysis-list', !isLoading);

  const filteredSubmissions = data?.items || [];

  // In-place question suggestions from the current page's rows (deduped) — shown
  // in the QuestionSearchFilter before the teacher types; free-text runs a
  // backend search. Picking one filters the list to that question.
  const questionSuggestions = useMemo<QuestionSearchOption[]>(() => {
    const seen = new Set<string>();
    const out: QuestionSearchOption[] = [];
    for (const s of filteredSubmissions) {
      if (!s.questionId || seen.has(s.questionId)) continue;
      seen.add(s.questionId);
      out.push({ questionId: s.questionId, questionText: s.question?.questionText });
    }
    return out;
  }, [filteredSubmissions]);

  const userSuggestions = useMemo(() => userSuggestionsFromRows(filteredSubmissions), [filteredSubmissions]);

  const { data: datasetsData } = useQuery({
    queryKey: ['evalDatasets'],
    queryFn: () => adminApi.getEvalDatasets(),
    enabled: showDatasetModal,
  });

  const importMutation = useMutation({
    mutationFn: ({ datasetId, submissionIds }: { datasetId: string; submissionIds: string[] }) =>
      adminApi.importDatasetItems(datasetId, { submissionIds, addedBy: 'admin' }),
    onSuccess: (result) => {
      setImportStatus({
        type: 'success',
        message: `Imported ${result.imported} submissions. Skipped: ${result.skipped}, Failed: ${result.failed}`,
      });
      setSelectedIds(new Set());
      setShowDatasetModal(false);
      setSelectedDatasetId('');
      queryClient.invalidateQueries({ queryKey: ['evalDatasets'] });
    },
    onError: (err) => {
      setImportStatus({ type: 'error', message: (err as Error).message });
    },
  });

  const getClientBadge = (clientInfo?: ClientInfo) => {
    if (!clientInfo?.platform) return null;
    const parts: string[] = [];
    if (clientInfo.deviceModel) parts.push(clientInfo.deviceModel);
    if (clientInfo.appVersion) parts.push(/^\d/.test(clientInfo.appVersion) ? 'v' + clientInfo.appVersion : clientInfo.appVersion);
    return (
      <span className="dc-client" title={`${clientInfo.platform} ${clientInfo.osVersion || ''}`}>
        {parts.length > 0 ? parts.join(', ') : clientInfo.platform}
      </span>
    );
  };

  const handleSelectAll = (submissions: Submission[]) => {
    if (selectedIds.size === submissions.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(submissions.map(s => s.id)));
  };

  const handleSelectOne = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const handleAddToDataset = () => {
    setShowDatasetModal(true);
    setImportStatus(null);
  };

  const handleImportConfirm = () => {
    if (!selectedDatasetId || selectedIds.size === 0) return;
    importMutation.mutate({ datasetId: selectedDatasetId, submissionIds: Array.from(selectedIds) });
  };

  const allSelected = filteredSubmissions.length > 0 && selectedIds.size === filteredSubmissions.length;
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const columns: ColumnDef<Submission>[] = useMemo(() => [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => handleSelectAll(filteredSubmissions)}
          title="Select all"
        />
      ),
      className: 'dt-checkbox-col',
      render: (s) => (
        <span onClick={(e) => handleSelectOne(s.id, e as unknown as React.MouseEvent)}>
          <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => {}} />
        </span>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      render: (s) => (
        <div className="dc-stack">
          <span className="dc-date">{format(new Date(s.submittedAt), 'MMM d, HH:mm')}</span>
          {s.status === 'analyzing' && (
            <span className="dc-status dc-status--analyzing" title="Analysis in flight">ANALYZING</span>
          )}
          {s.status === 'failed' && (
            <span className="dc-status dc-status--failed" title="Analysis failed">FAILED</span>
          )}
          {s.rerunFromSubmissionId && (
            <span className="dc-status dc-status--rerun" title={`Rerun of ${s.rerunFromSubmissionId}`}>RERUN</span>
          )}
        </div>
      ),
    },
    {
      key: 'user',
      header: 'User / Client',
      render: (s) => (
        <div className="dc-stack">
          <span className="dc-name">{s.user?.name || 'Unknown'}</span>
          <div className="dc-stack-secondary">
            <span className="dc-email">{s.user?.email || '-'}</span>
            {' '}{getClientBadge(s.clientInfo)}
          </div>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (s) => {
        const subj = s.question?.subject || '';
        return (
          <div className="dc-stack">
            <span className={`dc-subject ${subj ? `dc-subject--${subj}` : ''}`}>{subj || '-'}</span>
            <span className="dc-topic">{s.question?.topic || '-'}</span>
          </div>
        );
      },
    },
    {
      key: 'question',
      header: 'Question',
      render: (s) => {
        if (!s.question?.questionText) return <span className="dc-no-value">-</span>;
        const number = paperNumberLabel(s.question.sourceQuestionNumber);
        return (
          <>
            {number && <strong>Q{number}. </strong>}
            <LatexText text={buildQuestionDisplayText(s.question)} truncate={100} />
          </>
        );
      },
    },
    {
      key: 'score',
      header: 'Score',
      // Denormalized on the submission at analyze time (and re-copied on every
      // re-evaluation) — the same field the score-range filter queries. Reading
      // it here lets the list skip the modelOutputs join entirely. Legacy rows
      // from before the cutover have no `computedScores` and show '-'.
      render: (s) => <span className="dc-score">{s.computedScores?.finalScore ?? '-'}/10</span>,
    },
    {
      key: 'cost',
      header: 'Cost',
      // Summed at analyze time onto the submission, for the same reason as the
      // score above: modelOutputs is partitioned by user, so summing per
      // submission at read time is a cross-partition GROUP BY that a list page
      // cannot afford. Rows from before this landed, and failures, show '-'.
      render: (s) => (
        <span className="dc-cost">
          {typeof s.totalCostUsd === 'number' ? `$${s.totalCostUsd.toFixed(4)}` : '-'}
        </span>
      ),
    },
    {
      key: 'escalations',
      header: 'Esc.',
      // How many runs a quality check rejected and re-fired at a stronger
      // tier. Stamped with cost, same reasoning. 0 is the common case and is
      // shown quietly; anything above it is what an operator scans for.
      render: (s) => (
        typeof s.escalationCount === 'number'
          ? <span className={`dc-escalations${s.escalationCount > 0 ? ' dc-escalations--some' : ''}`}
                  title={s.escalationCount === 1 ? '1 run escalated to a stronger tier' : `${s.escalationCount} runs escalated to a stronger tier`}>
              {s.escalationCount}
            </span>
          : <span className="dc-cost">-</span>
      ),
    },
    {
      key: 'feedback',
      header: 'Feedback',
      render: (s) => Array.isArray(s.userFeedback) && s.userFeedback.length > 0 ? (
        <span className="dc-feedback" title={s.userFeedback.map(f => `${f.role}: ${f.rating}`).join(', ')}>
          {s.userFeedback.map((f, i) => (
            <span key={i} className="dc-feedback-dot">
              {f.rating === 'positive' ? '👍' : '👎'}
            </span>
          ))}
        </span>
      ) : (
        <span className="dc-no-value">—</span>
      ),
    },
    {
      key: 'input',
      header: 'Input',
      render: (s) => <span className="dc-tag">{s.inputMode || '-'}</span>,
    },
  ], [allSelected, filteredSubmissions, selectedIds]);

  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <>
      {importStatus && (
        <div className={`import-status ${importStatus.type}`}>
          {importStatus.message}
          <button className="close-status" onClick={() => setImportStatus(null)}>×</button>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="analysis-selection-row">
          <button className="action-btn analysis-add-to-dataset" onClick={handleAddToDataset}>
            Add {selectedIds.size} to Dataset
          </button>
        </div>
      )}

      <OperationsFilterBar
        values={appliedFilters as unknown as Record<string, string>}
        onChange={handleFiltersChange}
        extraDefs={extraDefs}
        onUserChange={(userId) => {
          // A pick filters immediately (like the question filter, unlike the
          // drafted select filters which wait for Fetch).
          const next = { ...appliedFilters, userId };
          setAppliedFilters(next);
          setPage(1);
          syncOperationsFiltersToUrl(setSearchParams, next, 1, OP);
        }}
        userSuggestions={userSuggestions}
        loading={isFetching}
        trailingActions={data ? (
          <Pagination page={page} totalPages={totalPages} total={data.total} onPageChange={handlePageChange} />
        ) : undefined}
      >
        <QuestionSearchFilter
            includeOwned
          value={appliedFilters.questionId}
          onChange={(qid) => {
            // A pick filters immediately (unlike the drafted text/select filters
            // which wait for Fetch).
            const next = { ...appliedFilters, questionId: qid };
            setAppliedFilters(next);
            setPage(1);
            syncOperationsFiltersToUrl(setSearchParams, next, 1, OP);
          }}
          suggestions={questionSuggestions}
          suggestionsLabel="Questions on this page"
          valueText={questionSuggestions.find(o => o.questionId === appliedFilters.questionId)?.questionText}
          subject={appliedFilters.subject || undefined}
          placeholder="Search question…"
        />
      </OperationsFilterBar>

      <DataTable
        columns={columns}
        // Right-click narrows to what the row is an example of. Same immediate
        // apply as the user and question pickers above — these ARE those two
        // filters, reached from a row instead of a search box.
        rowMenu={rowMenu}
        data={filteredSubmissions}
        rowKey={(s) => s.id}
        onRowClick={(s) => openRow(filteredSubmissions, s, '/submissions', { page, backSearch: searchParams.toString() })}
        rowHref={(s) => `/submissions/${s.id}`}
        rowClassName={(s) => [
          s.rerunFromSubmissionId ? 'rerun-row' : '',
          selectedIds.has(s.id) ? 'dt-row--selected' : '',
        ].filter(Boolean).join(' ')}
        loading={isLoading}
        emptyMessage="No submissions found"
      />

      {showDatasetModal && (
        <div className="modal-overlay">
          <div className="modal-content dataset-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add to Eval Dataset</h2>
              <button className="close-btn" onClick={() => setShowDatasetModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Add {selectedIds.size} selected submission{selectedIds.size !== 1 ? 's' : ''} to an evaluation dataset.
              </p>
              <div className="form-group">
                <label>Select Dataset</label>
                <select
                  value={selectedDatasetId}
                  onChange={(e) => setSelectedDatasetId(e.target.value)}
                  className="dataset-select"
                >
                  <option value="">Choose a dataset...</option>
                  {datasetsData?.datasets.map((dataset: EvalDataset) => (
                    <option key={dataset.id} value={dataset.id}>
                      {dataset.name} ({dataset.readableId}) - {dataset.metadata.inputCount} items
                    </option>
                  ))}
                </select>
              </div>
              {selectedDatasetId && datasetsData?.datasets && (
                <div className="dataset-preview">
                  {(() => {
                    const ds = datasetsData.datasets.find((d: EvalDataset) => d.id === selectedDatasetId);
                    return ds ? (
                      <>
                        <div className="preview-row">
                          <span className="preview-label">Category:</span>
                          <span className="category-badge">{ds.category}</span>
                        </div>
                        <div className="preview-row">
                          <span className="preview-label">Description:</span>
                          <span>{ds.description || 'No description'}</span>
                        </div>
                        <div className="preview-row">
                          <span className="preview-label">Current Items:</span>
                          <span>{ds.metadata.inputCount} ({ds.metadata.labeledCount} labeled)</span>
                        </div>
                      </>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowDatasetModal(false)}>
                Cancel
              </button>
              <button
                className="save-btn"
                onClick={handleImportConfirm}
                disabled={!selectedDatasetId || importMutation.isPending}
              >
                {importMutation.isPending ? 'Importing...' : 'Add to Dataset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
